import type { ReactNode } from 'react';
import {
  SiAndroid,
  SiApple,
  SiFacebook,
  SiFirefox,
  SiGoogleads,
  SiGooglechrome,
  SiInstagram,
  SiLinux,
  SiMessenger,
  SiOpera,
  SiSafari,
  SiSnapchat,
  SiTelegram,
  SiTiktok,
  SiWhatsapp,
  SiX,
  SiYoutube,
} from 'react-icons/si';
import {
  AppWindow,
  Bot,
  Flag,
  Globe,
  HelpCircle,
  Laptop,
  Mail,
  MessageSquare,
  Newspaper,
  Printer,
  Smartphone,
  Tablet,
  User,
} from 'lucide-react';
import type { BreakdownDim } from '@/lib/types';

// Country code → flag emoji. Returns a neutral white flag for unknown/'XX'.
export function countryFlag(code: string): string {
  if (!code || code.length !== 2 || code.toUpperCase() === 'XX') return '🏳️';
  const upper = code.toUpperCase();
  return String.fromCodePoint(
    upper.charCodeAt(0) + 0x1f1a5,
    upper.charCodeAt(1) + 0x1f1a5,
  );
}

// Friendly label for known keys; falls back to the raw key.
const PLATFORM_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
  other: 'Other',
  unknown: 'Unknown',
};
const DEVICE_LABELS: Record<string, string> = {
  Desktop: 'Desktop',
  Phone: 'Phone',
  Tablet: 'Tablet',
  Robot: 'Robot',
  unknown: 'Unknown',
};

// Lazy-instantiated; Intl.DisplayNames is cheap but not free.
let countryDisplay: Intl.DisplayNames | null = null;
function countryName(code: string): string {
  if (!code || code.length !== 2 || code.toUpperCase() === 'XX') return 'Unknown';
  if (!countryDisplay) {
    countryDisplay = new Intl.DisplayNames(['en'], { type: 'region' });
  }
  return countryDisplay.of(code.toUpperCase()) ?? code;
}

export function labelFor(dim: BreakdownDim, key: string): string {
  switch (dim) {
    case 'platform':
    case 'target_platform':
      return PLATFORM_LABELS[key] ?? key;
    case 'device_class':
      return DEVICE_LABELS[key] ?? key;
    case 'country':
      return countryName(key);
    case 'source':
      return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
    default:
      return key;
  }
}

// Map a key (lower-cased) to a Simple-Icons component. Returning null lets the
// caller fall back to a generic Lucide icon.
function brandIconFor(name: string): ReactNode | null {
  switch (name.toLowerCase()) {
    case 'chrome':
    case 'google chrome':
      return <SiGooglechrome className="text-[#4285F4]" />;
    case 'firefox':
    case 'firefox mobile':
      return <SiFirefox className="text-[#FF7139]" />;
    case 'safari':
    case 'safari mobile':
      return <SiSafari className="text-[#0FB5EE]" />;
    case 'opera':
      return <SiOpera className="text-[#FF1B2D]" />;
    case 'instagram':
      return <SiInstagram className="text-[#E4405F]" />;
    case 'facebook':
    case 'facebook_messenger':
      return <SiFacebook className="text-[#1877F2]" />;
    case 'messenger':
      return <SiMessenger className="text-[#00B2FF]" />;
    case 'tiktok':
      return <SiTiktok />;
    case 'telegram':
      return <SiTelegram className="text-[#26A5E4]" />;
    case 'whatsapp':
      return <SiWhatsapp className="text-[#25D366]" />;
    case 'snapchat':
      return <SiSnapchat className="text-[#FFFC00]" />;
    case 'twitter':
    case 'x':
      return <SiX />;
    case 'youtube':
      return <SiYoutube className="text-[#FF0000]" />;
    case 'website_banner':
    case 'influencer':
      return null;
    default:
      return null;
  }
}

export function iconFor(dim: BreakdownDim, key: string): ReactNode {
  const cls = 'h-4 w-4 shrink-0';

  // Country flag emoji rendered as inline text — no SVG.
  if (dim === 'country') {
    return (
      <span className="text-base leading-none" aria-hidden>
        {countryFlag(key)}
      </span>
    );
  }

  if (dim === 'platform' || dim === 'target_platform') {
    switch (key) {
      case 'ios':
        return <SiApple className={cls} />;
      case 'android':
        return <SiAndroid className={cls + ' text-[#3DDC84]'} />;
      case 'web':
        return <Globe className={cls} />;
      default:
        return <HelpCircle className={cls + ' text-muted-foreground'} />;
    }
  }

  if (dim === 'os') {
    switch (key.toLowerCase()) {
      case 'ios':
      case 'mac os x':
      case 'macos':
        return <SiApple className={cls} />;
      case 'android':
        return <SiAndroid className={cls + ' text-[#3DDC84]'} />;
      case 'windows':
      case 'windows nt':
        return <AppWindow className={cls + ' text-[#0078D6]'} />;
      case 'linux':
      case 'ubuntu':
        return <SiLinux className={cls} />;
      default:
        return <HelpCircle className={cls + ' text-muted-foreground'} />;
    }
  }

  if (dim === 'browser') {
    const brand = brandIconFor(key);
    if (brand) return <span className={cls + ' inline-flex'}>{brand}</span>;
    return <Globe className={cls + ' text-muted-foreground'} />;
  }

  if (dim === 'device_class') {
    switch (key) {
      case 'Desktop':
        return <Laptop className={cls} />;
      case 'Phone':
        return <Smartphone className={cls} />;
      case 'Tablet':
        return <Tablet className={cls} />;
      case 'Robot':
        return <Bot className={cls + ' text-muted-foreground'} />;
      default:
        return <HelpCircle className={cls + ' text-muted-foreground'} />;
    }
  }

  if (dim === 'webview_app') {
    const brand = brandIconFor(key);
    if (brand) return <span className={cls + ' inline-flex'}>{brand}</span>;
    return <AppWindow className={cls + ' text-muted-foreground'} />;
  }

  if (dim === 'source') {
    const brand = brandIconFor(key);
    if (brand) return <span className={cls + ' inline-flex'}>{brand}</span>;
    switch (key) {
      case 'email':
        return <Mail className={cls} />;
      case 'sms':
        return <MessageSquare className={cls} />;
      case 'print':
        return <Printer className={cls} />;
      case 'influencer':
        return <User className={cls} />;
      case 'website_banner':
        return <Newspaper className={cls} />;
      case 'google_ads':
        return <SiGoogleads className={cls + ' text-[#4285F4]'} />;
      case 'other':
      default:
        return <Flag className={cls + ' text-muted-foreground'} />;
    }
  }

  return <HelpCircle className={cls + ' text-muted-foreground'} />;
}
